import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { 
  Building2, 
  Briefcase, 
  Mail, 
  Phone, 
  Globe, 
  MapPin,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  ImageIcon
} from 'lucide-react';

const CATEGORY_OPTIONS = [
  { value: 'Primary', label: 'Primary', color: 'bg-blue-100 text-blue-700' },
  { value: 'Secondary', label: 'Secondary', color: 'bg-purple-100 text-purple-700' },
  { value: 'Additional 1', label: 'Additional 1', color: 'bg-green-100 text-green-700' },
  { value: 'Additional 2', label: 'Additional 2', color: 'bg-orange-100 text-orange-700' },
  { value: 'Additional 3', label: 'Additional 3', color: 'bg-pink-100 text-pink-700' },
];

const AffiliationField = ({ affiliations = [], onChange, maxAffiliations = 5 }) => {
  const [expandedId, setExpandedId] = useState(null);

  const addAffiliation = () => {
    if (affiliations.length >= maxAffiliations) return;
    
    // Determine the next category
    const usedCategories = affiliations.map(a => a.category);
    const nextCategory = CATEGORY_OPTIONS.find(c => !usedCategories.includes(c.value))?.value || 'Additional 3';
    
    const newAffiliation = {
      id: `aff-${Date.now()}`,
      category: affiliations.length === 0 ? 'Primary' : nextCategory,
      company: '',
      company_logo: null,
      designation: '',
      email: '',
      phone: '',
      website: '',
      address: '',
    };
    
    onChange([...affiliations, newAffiliation]);
    setExpandedId(newAffiliation.id);
  };

  const updateAffiliation = (id, field, value) => {
    onChange(affiliations.map(aff => 
      aff.id === id ? { ...aff, [field]: value } : aff
    ));
  };

  const removeAffiliation = (id) => {
    onChange(affiliations.filter(aff => aff.id !== id));
    if (expandedId === id) setExpandedId(null);
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getCategoryColor = (category) => {
    return CATEGORY_OPTIONS.find(c => c.value === category)?.color || 'bg-gray-100 text-gray-700';
  };

  const handleLogoUpload = async (id, file) => {
    if (!file) return;
    
    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      updateAffiliation(id, 'company_logo', e.target.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-base font-semibold">Company Affiliations</Label>
          <p className="text-sm text-muted-foreground">
            Add up to {maxAffiliations} company affiliations with their specific contact details
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addAffiliation}
          disabled={affiliations.length >= maxAffiliations}
          className="gap-2"
          data-testid="add-affiliation-btn"
        >
          <Plus className="w-4 h-4" />
          Add Affiliation
        </Button>
      </div>

      {affiliations.length === 0 ? (
        <div className="border-2 border-dashed rounded-lg p-8 text-center">
          <Building2 className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No affiliations added yet</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addAffiliation}
            className="mt-3 gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Your First Affiliation
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {affiliations.map((affiliation, index) => (
            <Card 
              key={affiliation.id} 
              className={`transition-all ${expandedId === affiliation.id ? 'ring-2 ring-primary/20' : ''}`}
            >
              <CardHeader 
                className="py-3 px-4 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => toggleExpand(affiliation.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={getCategoryColor(affiliation.category)}>
                      {affiliation.category}
                    </Badge>
                    <div>
                      <h4 className="font-medium text-sm">
                        {affiliation.company || 'Untitled Company'}
                      </h4>
                      {affiliation.designation && (
                        <p className="text-xs text-muted-foreground">{affiliation.designation}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeAffiliation(affiliation.id);
                      }}
                      data-testid={`remove-affiliation-${index}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                    {expandedId === affiliation.id ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </CardHeader>

              {expandedId === affiliation.id && (
                <CardContent className="pt-0 pb-4 px-4 space-y-4">
                  <Separator />
                  
                  {/* Category Selection */}
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={affiliation.category}
                      onValueChange={(value) => updateAffiliation(affiliation.id, 'category', value)}
                    >
                      <SelectTrigger data-testid={`affiliation-category-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORY_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Company & Designation */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Company Name
                      </Label>
                      <Input
                        placeholder="Company name"
                        value={affiliation.company}
                        onChange={(e) => updateAffiliation(affiliation.id, 'company', e.target.value)}
                        data-testid={`affiliation-company-${index}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        Designation / Title
                      </Label>
                      <Input
                        placeholder="Job title or role"
                        value={affiliation.designation}
                        onChange={(e) => updateAffiliation(affiliation.id, 'designation', e.target.value)}
                        data-testid={`affiliation-designation-${index}`}
                      />
                    </div>
                  </div>

                  {/* Company Logo */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      Company Logo
                    </Label>
                    <div className="flex items-center gap-4">
                      {affiliation.company_logo ? (
                        <div className="relative">
                          <img 
                            src={affiliation.company_logo} 
                            alt="Company logo" 
                            className="w-16 h-16 object-contain rounded-lg border"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-6 w-6"
                            onClick={() => updateAffiliation(affiliation.id, 'company_logo', null)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <label className="flex items-center justify-center w-16 h-16 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                          <ImageIcon className="w-6 h-6 text-muted-foreground" />
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleLogoUpload(affiliation.id, e.target.files?.[0])}
                          />
                        </label>
                      )}
                      <p className="text-xs text-muted-foreground">Optional company logo for this affiliation</p>
                    </div>
                  </div>

                  {/* Contact Details */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email
                      </Label>
                      <Input
                        type="email"
                        placeholder="work@company.com"
                        value={affiliation.email || ''}
                        onChange={(e) => updateAffiliation(affiliation.id, 'email', e.target.value)}
                        data-testid={`affiliation-email-${index}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        Phone
                      </Label>
                      <Input
                        type="tel"
                        placeholder="+1 234 567 8900"
                        value={affiliation.phone || ''}
                        onChange={(e) => updateAffiliation(affiliation.id, 'phone', e.target.value)}
                        data-testid={`affiliation-phone-${index}`}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Website
                    </Label>
                    <Input
                      type="url"
                      placeholder="https://company.com"
                      value={affiliation.website || ''}
                      onChange={(e) => updateAffiliation(affiliation.id, 'website', e.target.value)}
                      data-testid={`affiliation-website-${index}`}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Address
                    </Label>
                    <Textarea
                      placeholder="Office address"
                      rows={2}
                      value={affiliation.address || ''}
                      onChange={(e) => updateAffiliation(affiliation.id, 'address', e.target.value)}
                      data-testid={`affiliation-address-${index}`}
                    />
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {affiliations.length > 0 && affiliations.length < maxAffiliations && (
        <p className="text-xs text-muted-foreground text-center">
          {maxAffiliations - affiliations.length} more affiliation{maxAffiliations - affiliations.length !== 1 ? 's' : ''} can be added
        </p>
      )}
    </div>
  );
};

export default AffiliationField;
